<script setup lang="ts">
// The Filters tool. It is the ledger's filter chrome now that the second header
// is gone: a Show/Hide toggle for the typed filter row under the active table's
// column labels, a Clear that resets the whole spec, and the list of clauses
// actually narrowing.
//
// The controls themselves stay under the ledger headers, where each column's own
// control (text, number range, date range, enum) is the one the server's
// capability declares for it. This pane does not duplicate them: it makes them
// reachable, and it names the active table so the toggle cannot be mistaken for
// one table's filters while another is showing.
import { computed } from "vue";

import { useStoreMapReview } from "@/store/map-review";

import { MAP_REVIEW_TABLE_LABELS } from "./columns";

const store = useStoreMapReview();

const tableLabel = computed(() => MAP_REVIEW_TABLE_LABELS[store.activeTable]);
</script>

<template>
	<div class="control-pane">
		<!-- The ledger's own filter controls: toggled here, drawn under the
		     column labels. The table is named because the toggle always acts on
		     the active table — the ledger holds one table at a time. -->
		<div class="control-pane-filter-head">
			<span class="control-pane-tool-label">{{ tableLabel }} filters</span>
			<p class="control-pane-filter-help">
				The controls appear under the {{ tableLabel }} column headers, one per
				column, in the form the server declares for it. They narrow the table,
				the counts and the map together.
			</p>
			<div class="control-pane-filter-actions">
				<button
					type="button"
					class="control-pane-btn"
					:aria-expanded="store.filtersOpen"
					@click="store.toggleFiltersOpen()"
				>
					<i
						class="bi"
						:class="store.filtersOpen ? 'bi-funnel-fill' : 'bi-funnel'"
						aria-hidden="true"
					></i>
					{{
						store.filtersOpen ? "Hide filter controls" : "Show filter controls"
					}}
				</button>
				<button
					type="button"
					class="control-pane-btn"
					:disabled="!store.filtersActive"
					title="Clear every applied filter and search"
					@click="store.clearFilters()"
				>
					<i class="bi bi-x-circle" aria-hidden="true"></i>
					Clear filters
				</button>
			</div>
		</div>

		<!-- What is applied, read from the store's own summary rather than
		     rebuilt here: the same list feeds the Analysis pane's indicator, so
		     the two cannot disagree about what is narrowing. Each row removes
		     exactly itself. -->
		<div class="control-pane-applied">
			<div class="control-pane-applied-head">
				<span class="control-pane-tool-label">Applied filters</span>
			</div>
			<ul
				v-if="store.appliedFilters.length > 0"
				class="control-pane-applied-list"
			>
				<li
					v-for="row in store.appliedFilters"
					:key="row.key"
					class="control-pane-applied-row"
				>
					<span class="control-pane-applied-text">
						<span class="control-pane-applied-label">{{ row.label }}</span>
						<span v-if="row.detail" class="control-pane-applied-detail">
							{{ row.detail }}
						</span>
					</span>
					<button
						type="button"
						class="control-pane-applied-remove"
						:aria-label="`Remove filter: ${row.label}`"
						:title="`Remove ${row.label}`"
						@click="store.clearApplied(row.key)"
					>
						<i class="bi bi-x" aria-hidden="true"></i>
					</button>
				</li>
			</ul>
			<p v-else class="control-pane-applied-empty">
				No filters applied; the map and every ledger show the whole district.
			</p>
		</div>
	</div>
</template>

<style scoped>
/* The pane's own base: the rail body owns the box — its width, background,
   padding and scroll — so the pane is only its content and its type scale. The
   `.control-pane-*` names are the rail panes' own, re-declared here because a
   scoped rule never reaches into another component's markup. */
.control-pane {
	font-size: 0.8125rem;
}

/* The toggle and clear actions, named for the table they act on. They sit above
   the applied list because they are the pane's tools; the list below is what the
   tools have done. */
.control-pane-filter-head {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding-bottom: 8px;
	border-bottom: 1px solid #f1f3f5;
}

.control-pane-filter-help {
	margin: 0;
	color: #6c757d;
	font-size: 0.75rem;
	line-height: 1.4;
}

.control-pane-filter-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 2px;
}

.control-pane-btn {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.control-pane-btn:hover:not(:disabled) {
	background: #f1f3f5;
}

.control-pane-btn:disabled {
	color: #adb5bd;
}

/* The applied list: a header row, then one row per clause. The rows are the same
   shape as the tool rows above them, so the pane reads as one column of
   statements rather than two densities. */
.control-pane-applied {
	margin-top: 8px;
}

.control-pane-applied-head {
	display: flex;
	align-items: center;
	gap: 6px;
	padding-bottom: 2px;
}

.control-pane-tool-label {
	font-weight: 600;
	color: #495057;
	flex-shrink: 0;
}

.control-pane-applied-list {
	list-style: none;
	margin: 4px 0 0;
	padding: 0;
}

.control-pane-applied-row {
	display: flex;
	align-items: flex-start;
	gap: 4px;
	padding: 3px 0;
	border-bottom: 1px solid #f8f9fa;
}

.control-pane-applied-row:last-child {
	border-bottom: 0;
}

/* The clause's own text wraps rather than truncating: the detail is the value
   the operator typed, and an ellipsis would hide the very thing they are
   checking. */
.control-pane-applied-text {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-width: 0;
	word-break: break-word;
}

.control-pane-applied-label {
	color: #343a40;
}

.control-pane-applied-detail {
	color: #6c757d;
	font-size: 0.75rem;
}

.control-pane-applied-remove {
	flex: 0 0 auto;
	border: 0;
	background: none;
	padding: 0 2px;
	line-height: 1;
	color: #adb5bd;
}

.control-pane-applied-remove:hover {
	color: #dc3545;
}

.control-pane-applied-empty {
	margin: 4px 0 0;
	color: #6c757d;
	font-size: 0.75rem;
	line-height: 1.4;
}
</style>
